import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Leaf, MapPin, Snowflake, Sun, TreePine, Flower2, ChefHat, Scale, Thermometer, Flame } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types & Data
// ---------------------------------------------------------------------------

const MONTH_NAMES = [
  'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
  'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic',
];

interface Seta {
  name: string;
  scientific: string;
  peak: number[];       // months 1-12
  available: number[];  // months 1-12
  priceRange: string;   // e.g. "25-60"
  mermaLimpieza: string;
  mermaCoccion: string;
  notes: string;
}

const SETAS: Seta[] = [
  { name: 'Boletus edulis (cep/porcini)', scientific: 'Boletus edulis', peak: [10, 11], available: [9, 10, 11], priceRange: '25-60', mermaLimpieza: '15-20%', mermaCoccion: '30-35%', notes: 'El rey de las setas. Aroma intenso a bosque, textura carnosa. Mejor en revueltos, risottos o a la plancha.' },
  { name: 'Niscalo / Rovello', scientific: 'Lactarius deliciosus', peak: [10, 11], available: [9, 10, 11, 12], priceRange: '8-20', mermaLimpieza: '10-15%', mermaCoccion: '25-30%', notes: 'La seta mas popular de Espana. A la plancha con ajo y perejil es un clasico imbatible.' },
  { name: 'Rebozuelo / Chantarelle', scientific: 'Cantharellus cibarius', peak: [9, 10, 11], available: [6, 7, 8, 9, 10, 11], priceRange: '15-35', mermaLimpieza: '10-15%', mermaCoccion: '25-30%', notes: 'Aroma afrutado unico. Dorados, perfumados, ideales como guarnicion premium.' },
  { name: 'Trufa negra', scientific: 'Tuber melanosporum', peak: [12, 1, 2], available: [11, 12, 1, 2, 3], priceRange: '400-1.200', mermaLimpieza: '15-25%', mermaCoccion: '0% (en crudo)', notes: 'El diamante negro de la gastronomia. Siempre en crudo o rallada al final. Justifica suplementos de 15-25 EUR.' },
  { name: 'Trufa de verano', scientific: 'Tuber aestivum', peak: [6, 7, 8], available: [5, 6, 7, 8, 9], priceRange: '80-250', mermaLimpieza: '15-20%', mermaCoccion: '0% (en crudo)', notes: 'Mas suave que la negra, pero excelente relacion calidad-precio para hosteleria.' },
  { name: 'Colmenilla / Morchella', scientific: 'Morchella esculenta', peak: [3, 4, 5], available: [3, 4, 5], priceRange: '40-80', mermaLimpieza: '15-20%', mermaCoccion: '30-35%', notes: 'Seta de primavera con textura alveolada unica. Siempre cocinar bien. Combina perfecto con cremas y carnes.' },
  { name: 'Seta de cardo', scientific: 'Pleurotus eryngii', peak: [10, 11], available: [9, 10, 11, 12, 3, 4], priceRange: '10-25', mermaLimpieza: '10-15%', mermaCoccion: '25-30%', notes: 'Textura firme y carnosa. Ideal a la plancha, confitada o en tempura. Versatil y rentable.' },
  { name: 'Champinon silvestre', scientific: 'Agaricus campestris', peak: [9, 10, 11], available: [9, 10, 11], priceRange: '8-15', mermaLimpieza: '5-10%', mermaCoccion: '25-35%', notes: 'Sabor mas intenso que el cultivado. Diferencia notable en crudo para ensaladas.' },
  { name: 'Pie azul', scientific: 'Lepista nuda', peak: [11, 12, 1], available: [10, 11, 12, 1, 2], priceRange: '12-25', mermaLimpieza: '10-15%', mermaCoccion: '25-30%', notes: 'Color violaceo llamativo. Sabor delicado, ideal para guisos de invierno y como guarnicion.' },
  { name: 'Senderuela', scientific: 'Marasmius oreades', peak: [4, 5, 6], available: [3, 4, 5, 6, 10, 11], priceRange: '15-30', mermaLimpieza: '10-15%', mermaCoccion: '25-30%', notes: 'Pequeña pero con sabor concentrado. Se seca facilmente y rehidrata sin perder aroma.' },
  { name: 'Parasol / Galamperna', scientific: 'Macrolepiota procera', peak: [9, 10, 11], available: [9, 10, 11], priceRange: '10-20', mermaLimpieza: '20-30%', mermaCoccion: '25-30%', notes: 'Sombrero grande ideal para rebozar como un escalope vegetal. Presentacion espectacular.' },
  { name: 'Trompeta de la muerte', scientific: 'Craterellus cornucopioides', peak: [10, 11], available: [9, 10, 11, 12], priceRange: '20-45', mermaLimpieza: '15-20%', mermaCoccion: '30-35%', notes: 'A pesar del nombre, es deliciosa. Color negro intenso que aporta dramatismo al plato.' },
  { name: 'Oreja de Judas', scientific: 'Auricularia auricula-judae', peak: [10, 11, 12], available: [9, 10, 11, 12, 1], priceRange: '8-15', mermaLimpieza: '10-15%', mermaCoccion: '15-20%', notes: 'Textura gelatinosa unica. Muy usada en cocina asiatica. Interesante para fusion.' },
  { name: 'Shiitake (cultivado)', scientific: 'Lentinula edodes', peak: [], available: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12], priceRange: '8-18', mermaLimpieza: '10-15%', mermaCoccion: '25-30%', notes: 'Disponible todo el ano. Sabor umami potente. Imprescindible para caldos y salteados.' },
  { name: 'Seta de chopo', scientific: 'Cyclocybe aegerita', peak: [11, 12, 1, 2], available: [10, 11, 12, 1, 2, 3], priceRange: '12-25', mermaLimpieza: '10-15%', mermaCoccion: '25-30%', notes: 'Seta de invierno con aroma a harina fresca. Excelente en revueltos y arroces.' },
];

// ---------------------------------------------------------------------------
// Season data
// ---------------------------------------------------------------------------

interface SeasonInfo {
  name: string;
  icon: typeof Leaf;
  color: string;
  bg: string;
  months: string;
  setas: { name: string; desc: string }[];
}

const SEASONS: SeasonInfo[] = [
  {
    name: 'Primavera',
    icon: Flower2,
    color: 'text-lime-700',
    bg: 'bg-lime-50 border-lime-200',
    months: 'Marzo - Mayo',
    setas: [
      { name: 'Colmenillas (morchella)', desc: 'Seta gourmet de primavera. Alveolada, unica en sabor. Siempre bien cocinada. Margen alto en carta.' },
      { name: 'Senderuelas', desc: 'Pequeñas, aromaticas, se secan facilmente. Ideales para conservar y usar fuera de temporada.' },
      { name: 'Setas de cardo', desc: 'Rebrotan en primavera con las lluvias. Textura carnosa, perfecta a la plancha.' },
      { name: 'Perrechicos', desc: 'Seta muy valorada en el Pais Vasco. Escasa y cara, pero con aroma y sabor incomparables.' },
    ],
  },
  {
    name: 'Verano',
    icon: Sun,
    color: 'text-amber-700',
    bg: 'bg-amber-50 border-amber-200',
    months: 'Junio - Agosto',
    setas: [
      { name: 'Trufa de verano (aestivum)', desc: 'Version veraniega mas suave. Precio accesible comparado con la trufa negra de invierno.' },
      { name: 'Rebozuelos', desc: 'Empiezan a aparecer con las lluvias de verano, especialmente en el norte.' },
      { name: 'Oronja (Amanita caesarea)', desc: 'La seta de los Cesares. Espectacular en crudo, laminada fina con aceite de oliva y parmesano.' },
    ],
  },
  {
    name: 'Otoño',
    icon: TreePine,
    color: 'text-orange-700',
    bg: 'bg-orange-50 border-orange-200',
    months: 'Septiembre - Noviembre',
    setas: [
      { name: 'Boletus edulis', desc: 'THE SEASON. El boletus de otoño es el producto estrella de la micologia española. Aroma, textura, versatilidad.' },
      { name: 'Niscalos', desc: 'La seta mas recolectada de España. Accesible, popular y deliciosa a la plancha con ajo.' },
      { name: 'Trompeta de la muerte', desc: 'Dramatica y deliciosa. Su color negro añade contraste visual a cualquier plato.' },
      { name: 'Rebozuelos', desc: 'En su pico de temporada. Dorados y perfumados, elevan cualquier guarnicion.' },
    ],
  },
  {
    name: 'Invierno',
    icon: Snowflake,
    color: 'text-blue-700',
    bg: 'bg-blue-50 border-blue-200',
    months: 'Diciembre - Febrero',
    setas: [
      { name: 'Trufa negra (melanosporum)', desc: 'El diamante negro. Producto ultra-premium que multiplica el ticket medio de cualquier restaurante.' },
      { name: 'Seta de chopo', desc: 'Aparece en troncos de chopos tras las primeras heladas. Aroma a harina, textura firme.' },
      { name: 'Pie azul', desc: 'Resiste el frio. Su color violaceo sorprende y su sabor delicado encaja en guisos de invierno.' },
    ],
  },
];

// ---------------------------------------------------------------------------
// Zonas seteras
// ---------------------------------------------------------------------------

const ZONAS = [
  { name: 'Soria', region: 'Castilla y Leon', desc: 'Capital mundial de la trufa negra y de los boletus. Las Jornadas de la Trufa de Abejar son referencia nacional. Pinares de Urbion producen niscalos y boletus de calidad excepcional.' },
  { name: 'Navarra', region: 'Navarra', desc: 'Hayedos de Irati y pinares del norte producen boletus, rebozuelos y senderuelas. El mercado de setas de Estella es un clasico del otoño.' },
  { name: 'Catalunya', region: 'Catalunya', desc: 'Los bosques del Bergueda, Solsones y Alt Urgell son paraiso micologico. Rovellons, fredolics, trompetes y ceps de altisima calidad.' },
  { name: 'Castilla y Leon', region: 'Castilla y Leon', desc: 'Ademas de Soria, los bosques de Segovia, Avila y Zamora producen niscalos, boletus y pie azul en cantidades enormes.' },
  { name: 'Pais Vasco', region: 'Pais Vasco', desc: 'Los perrechicos son la joya local. Los bosques de Gipuzkoa y Bizkaia tambien producen boletus de calidad y senderuelas.' },
  { name: 'Teruel', region: 'Aragon', desc: 'Junto con Soria, es la capital de la trufa negra española. Las tierras arcillosas de Sarrion producen las mejores trufas melanosporum del mundo.' },
];

// ---------------------------------------------------------------------------
// Conservacion
// ---------------------------------------------------------------------------

const CONSERVACION = [
  { method: 'Nevera (frescas)', duration: '3-5 dias', tips: 'En bolsa de papel o envueltas en paño humedo. Nunca en plastico cerrado — se pudren. Consumir lo antes posible.', icon: Thermometer },
  { method: 'Deshidratado', duration: '6-12 meses', tips: 'Laminar fino y secar al aire o en deshidratador a 50 grados C. Los boletus y rebozuelos secos concentran sabor. Rehidratar 20 min antes de usar.', icon: Sun },
  { method: 'Congelado', duration: '3-6 meses', tips: 'Saltear ligeramente antes de congelar para mantener textura. Las setas crudas congeladas quedan aguadas al descongelar.', icon: Snowflake },
  { method: 'En aceite / confitadas', duration: '2-3 meses', tips: 'Confitar en aceite de oliva a 70 grados C. Guardar en bote esterilizado. Ideal para tener setas premium fuera de temporada.', icon: Flame },
];

// ---------------------------------------------------------------------------
// Ideas para carta
// ---------------------------------------------------------------------------

const IDEAS_CARTA = [
  { tipo: 'Entrantes', ideas: ['Croquetas de boletus con trufa rallada', 'Carpaccio de cep crudo con parmesano y aceite de trufa', 'Tostas de rebozuelos con huevo de codorniz', 'Crema de setas silvestres con cebollino'] },
  { tipo: 'Guarniciones', ideas: ['Salteado de setas variadas con ajo y perejil', 'Niscalos a la plancha como guarnicion de carne', 'Duxelles de champinon para rellenos', 'Chips de seta de cardo deshidratada'] },
  { tipo: 'Platos principales', ideas: ['Risotto de boletus con parmesano 36 meses', 'Canelones de setas silvestres con bechamel trufada', 'Solomillo con salsa de trompetas de la muerte', 'Arroz meloso de setas y alcachofas'] },
  { tipo: 'Especiales de temporada', ideas: ['Menu degustacion micologico (octubre-noviembre)', 'Huevos rotos con trufa negra (diciembre-febrero)', 'Revuelto de perrechicos (abril-mayo)', 'Tabla de setas de temporada a la brasa'] },
];

// ---------------------------------------------------------------------------
// Cultivadas vs silvestres
// ---------------------------------------------------------------------------

const CULTIVADAS = [
  { name: 'Champinon comun', scientific: 'Agaricus bisporus', price: '2-5 EUR/kg', disponibilidad: 'Todo el ano', notes: 'La seta mas consumida del mundo. Margen alto, bajo coste. El Paris clasico.' },
  { name: 'Shiitake', scientific: 'Lentinula edodes', price: '8-18 EUR/kg', disponibilidad: 'Todo el ano', notes: 'Cultivado en troncos o sustratos. Umami potente. Imprescindible en cocina asiatica y fusion.' },
  { name: 'Seta de ostra (Pleurotus)', scientific: 'Pleurotus ostreatus', price: '4-10 EUR/kg', disponibilidad: 'Todo el ano', notes: 'Textura delicada, sabor suave. Muy versatil: salteada, en tempura, en crema. Bajo coste.' },
  { name: 'Enoki', scientific: 'Flammulina velutipes', price: '6-12 EUR/kg', disponibilidad: 'Todo el ano', notes: 'Largas y finas. Textura crujiente. Ideales para ensaladas, sopas y decoracion.' },
];

// ---------------------------------------------------------------------------
// FAQs
// ---------------------------------------------------------------------------

const faqs = [
  {
    q: 'Cual es la mejor temporada de setas en Espana?',
    a: 'La temporada principal de setas en Espana es el otoño (septiembre-noviembre), especialmente despues de lluvias seguidas de dias templados. Octubre es el mes estrella con boletus, niscalos, rebozuelos y trompetas de la muerte en su mejor momento. Sin embargo, hay setas en todas las estaciones: colmenillas en primavera, trufas de verano en julio-agosto y trufas negras en invierno.',
  },
  {
    q: 'Cuanto cuestan los boletus edulis frescos para hosteleria?',
    a: 'El precio del boletus edulis silvestre fresco oscila entre 25 y 60 EUR/kg segun la temporada, calidad y procedencia. Al inicio de la temporada (septiembre) suelen ser mas caros. En el pico de produccion (octubre-noviembre) los precios bajan. Para hosteleria, comprar directamente a recolectores locales o cooperativas puede reducir el coste un 20-30%.',
  },
  {
    q: 'Cual es la merma de las setas en cocina?',
    a: 'Las setas tienen una merma de limpieza del 10-30% segun la variedad (los boletus y trompetas son las que mas pierden) y una merma de coccion del 25-40% ya que contienen mucha agua. Esto significa que de 1 kg de setas compradas, pueden quedar 400-600 g aprovechables. Es fundamental incluir estas mermas en el escandallo para no perder dinero.',
  },
  {
    q: 'Como conservar setas frescas en un restaurante?',
    a: 'Las setas frescas duran 3-5 dias en nevera, envueltas en papel (nunca en plastico). Para conservarlas mas tiempo: deshidratar (6-12 meses), saltear y congelar (3-6 meses) o confitar en aceite (2-3 meses). La deshidratacion concentra el sabor y es la tecnica mas usada en hosteleria profesional.',
  },
  {
    q: 'Donde comprar setas silvestres de calidad para restaurante?',
    a: 'Las mejores opciones son: 1) Mercados mayoristas (Mercabarna, Mercamadrid) con proveedores especializados en setas. 2) Recolectores locales y cooperativas micologicas de Soria, Navarra o Catalunya. 3) Distribuidores especializados como SoriActiva o Micofora. Siempre verificar que tengan certificado de seguridad alimentaria.',
  },
  {
    q: 'Que setas se pueden usar todo el año en hosteleria?',
    a: 'Las setas cultivadas estan disponibles los 12 meses: champinon comun (2-5 EUR/kg), shiitake (8-18 EUR/kg), seta de ostra/pleurotus (4-10 EUR/kg) y enoki (6-12 EUR/kg). Ademas, las setas deshidratadas (boletus, rebozuelo, trompeta) se rehidratan facilmente y permiten ofrecer setas premium fuera de temporada.',
  },
  {
    q: 'Como incluir setas en la carta para maximizar el margen?',
    a: 'Las setas silvestres tienen alto valor percibido: un plato con boletus o trufa justifica precios premium. Estrategias: 1) Menus degustacion micologicos en otoño. 2) Suplementos de trufa (15-25 EUR) en platos existentes. 3) Usar setas como guarnicion premium en vez de plato principal (menos coste, mismo efecto). 4) Combinar setas silvestres con cultivadas para equilibrar coste.',
  },
  {
    q: 'Es rentable ofrecer trufa negra en un restaurante?',
    a: 'Si, la trufa negra es uno de los ingredientes mas rentables en hosteleria. Aunque cuesta 400-1.200 EUR/kg, se usa en cantidades minimas (5-10 g por plato). Un suplemento de trufa de 15-20 EUR con 5 g de trufa cuesta al restaurante 2-6 EUR en materia prima. El margen es del 70-85%, muy superior al de la mayoria de ingredientes.',
  },
];

// ---------------------------------------------------------------------------
// JSON-LD structured data
// ---------------------------------------------------------------------------

const jsonLdFaq = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const jsonLdBreadcrumb = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    { '@type': 'ListItem', position: 2, name: 'Calendario de temporada', item: 'https://pleko.app/calendario-temporada' },
    { '@type': 'ListItem', position: 3, name: 'Setas de temporada', item: 'https://pleko.app/setas-de-temporada' },
  ],
};

const jsonLdArticle = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Setas de Temporada en Espana: Calendario, Variedades y Precios para Hosteleria',
  description: 'Guia completa de setas de temporada en Espana con calendario anual, precios, mermas y consejos para hosteleria profesional.',
  author: { '@type': 'Organization', name: 'PLEKO', url: 'https://pleko.app' },
  publisher: { '@type': 'Organization', name: 'PLEKO', url: 'https://pleko.app' },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  mainEntityOfPage: 'https://pleko.app/setas-de-temporada',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function SetasTemporadaPage() {
  const currentMonth = new Date().getMonth() + 1;
  const [highlightMonth, setHighlightMonth] = useState(currentMonth);

  return (
    <>
      <Helmet>
        <title>Setas de Temporada en Espana 2026 — Calendario y Precios | PLEKO</title>
        <meta
          name="description"
          content="Calendario completo de setas de temporada en Espana: boletus, niscalos, rebozuelos, trufas y 15+ variedades con precios, mermas y consejos para hosteleria."
        />
        <meta
          name="keywords"
          content="setas de temporada, setas temporada espana, calendario setas, que setas hay en otoño, boletus temporada, niscalos temporada, rebozuelos temporada, trufa negra temporada, setas silvestres espana, precio boletus, precio trufa, merma setas"
        />
        <link rel="canonical" href="https://pleko.app/setas-de-temporada" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/setas-de-temporada" />
        <meta property="og:title" content="Setas de Temporada en Espana 2026 — Calendario y Precios | PLEKO" />
        <meta property="og:description" content="Calendario completo de setas de temporada en Espana: boletus, niscalos, rebozuelos, trufas y 15+ variedades. Guia para hosteleria." />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Setas de Temporada en Espana 2026 — Calendario y Precios | PLEKO" />
        <meta name="twitter:description" content="Calendario completo de setas de temporada en Espana con precios, mermas y consejos para hosteleria profesional." />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(jsonLdFaq)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdArticle)}</script>
      </Helmet>

      <div className="bg-white">
        {/* Hero */}
        <section className="bg-gradient-to-b from-amber-50 to-white py-16 lg:py-24">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                <Leaf className="w-4 h-4" />
                Calendario de setas de temporada en Espana — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                Setas de <span className="text-amber-700">Temporada</span> en Espana: Calendario, Variedades y Precios para Hosteleria
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-3xl mx-auto">
                Guia completa con las 15+ variedades de setas silvestres y cultivadas disponibles en Espana,
                su calendario de recogida, precios de mercado, mermas reales y consejos para incluirlas en tu carta.
              </p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Link
                  to="/calendario-temporada"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-100 text-amber-800 rounded-lg font-medium hover:bg-amber-200 transition-colors text-sm"
                >
                  <Leaf className="w-4 h-4" /> Calendario completo de temporada
                </Link>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-700 text-white rounded-lg font-medium hover:bg-amber-800 transition-colors text-sm"
                >
                  Prueba PLEKO gratis <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Section 1: Por que las setas son oro */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Por que las setas de temporada son oro para tu carta
          </h2>
          <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
            Las setas silvestres son uno de los productos con mayor valor percibido en hosteleria.
            Un ingrediente que transforma un plato ordinario en una experiencia premium.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: 'Producto premium',
                desc: 'Un plato con boletus o trufa justifica precios de 18-35 EUR. El comensal asocia setas silvestres con alta cocina y producto de calidad excepcional.',
                color: 'bg-amber-100 text-amber-700',
              },
              {
                title: 'Margen alto',
                desc: 'Las setas como guarnicion premium cuestan 1-3 EUR por racion pero permiten subir el PVP 5-10 EUR. La trufa rallada cuesta 2-6 EUR pero justifica suplementos de 15-25 EUR.',
                color: 'bg-orange-100 text-orange-700',
              },
              {
                title: 'Diferenciacion',
                desc: 'Una carta que ofrece setas de temporada transmite profesionalidad, conexion con el producto y estacionalidad. Pocos restaurantes lo hacen bien — es tu oportunidad.',
                color: 'bg-yellow-100 text-yellow-700',
              },
            ].map((b, i) => (
              <div key={i} className="text-center">
                <div className={`w-16 h-16 ${b.color} rounded-2xl flex items-center justify-center mx-auto mb-4`}>
                  <TreePine className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{b.title}</h3>
                <p className="text-slate-600">{b.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Section 2: GRAN TABLA — Calendario anual de setas */}
        <section className="bg-amber-50/50 py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Calendario anual de setas en Espana
            </h2>
            <p className="text-slate-600 text-center mb-6 max-w-2xl mx-auto">
              15 variedades de setas con sus meses de disponibilidad y pico de temporada.
              Las celdas oscuras indican el pico; las claras, disponibilidad.
            </p>

            {/* Month selector */}
            <div className="flex flex-wrap gap-2 justify-center mb-8">
              {MONTH_NAMES.map((name, idx) => {
                const m = idx + 1;
                const isCurrent = m === currentMonth;
                const isSelected = m === highlightMonth;
                return (
                  <button
                    key={m}
                    onClick={() => setHighlightMonth(m)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      isSelected
                        ? 'bg-amber-700 text-white shadow-md'
                        : isCurrent
                          ? 'bg-amber-100 text-amber-800 border border-amber-300'
                          : 'bg-white text-slate-600 hover:bg-amber-100'
                    }`}
                  >
                    {name}
                  </button>
                );
              })}
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-[800px] w-full text-sm bg-white rounded-xl border border-amber-200 overflow-hidden">
                <thead>
                  <tr className="bg-amber-100/80">
                    <th className="text-left py-3 px-4 text-slate-700 font-semibold w-52">Variedad</th>
                    {MONTH_NAMES.map((name, i) => (
                      <th
                        key={i}
                        className={`text-center py-3 px-1 text-xs font-semibold ${
                          i + 1 === highlightMonth ? 'text-amber-800 bg-amber-200/60' : 'text-slate-500'
                        }`}
                      >
                        {name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SETAS.map((seta, si) => (
                    <tr key={si} className={`border-t border-amber-100 ${si % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'}`}>
                      <td className="py-2 px-4 font-medium text-slate-800 text-xs sm:text-sm">
                        {seta.name}
                      </td>
                      {Array.from({ length: 12 }, (_, i) => {
                        const m = i + 1;
                        const isPeak = seta.peak.includes(m);
                        const isAvail = seta.available.includes(m);
                        const isHighlight = m === highlightMonth;
                        return (
                          <td key={m} className={`text-center py-2 px-1 ${isHighlight ? 'bg-amber-100/50' : ''}`}>
                            {isPeak ? (
                              <span className="inline-block w-5 h-5 rounded-full bg-amber-600" title={`${seta.name} — pico en ${MONTH_NAMES[i]}`} />
                            ) : isAvail ? (
                              <span className="inline-block w-5 h-5 rounded-full bg-amber-200" title={`${seta.name} — disponible en ${MONTH_NAMES[i]}`} />
                            ) : (
                              <span className="inline-block w-5 h-5 rounded-full bg-slate-50" />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-6 justify-center mt-4 text-sm text-slate-500">
              <span className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded-full bg-amber-600" /> Pico de temporada
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded-full bg-amber-200" /> Disponible
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded-full bg-slate-50 border border-slate-200" /> Fuera de temporada
              </span>
            </div>
          </div>
        </section>

        {/* Section 3: Setas estrella de cada estacion */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">
            Las setas estrella de cada estacion
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {SEASONS.map((season) => {
              const Icon = season.icon;
              return (
                <div key={season.name} className={`rounded-xl border p-6 ${season.bg}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <Icon className={`w-6 h-6 ${season.color}`} />
                    <div>
                      <h3 className={`text-xl font-bold ${season.color}`}>{season.name}</h3>
                      <p className="text-sm text-slate-500">{season.months}</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    {season.setas.map((s, i) => (
                      <div key={i}>
                        <p className="font-semibold text-slate-800 text-sm">{s.name}</p>
                        <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 4: Tabla de precios */}
        <section className="bg-slate-50 py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
              Precios de setas para hosteleria
            </h2>
            <p className="text-slate-600 text-center mb-8 max-w-2xl mx-auto">
              Precios orientativos por kilogramo en mercados mayoristas y proveedores especializados. Varian segun temporada, calidad y procedencia.
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm bg-white rounded-xl border border-slate-200 overflow-hidden">
                <thead>
                  <tr className="bg-slate-100">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Variedad</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-700">Precio EUR/kg</th>
                    <th className="text-center py-3 px-4 font-semibold text-slate-700">Temporada pico</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700 hidden sm:table-cell">Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {SETAS.map((seta, i) => (
                    <tr key={i} className={`border-t border-slate-100 ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}>
                      <td className="py-2.5 px-4 font-medium text-slate-800">{seta.name}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                          parseInt(seta.priceRange) > 100
                            ? 'bg-red-100 text-red-700'
                            : parseInt(seta.priceRange) > 20
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-green-100 text-green-700'
                        }`}>
                          {seta.priceRange} EUR/kg
                        </span>
                      </td>
                      <td className="py-2.5 px-4 text-center text-slate-600 text-xs">
                        {seta.peak.length > 0
                          ? seta.peak.map((m) => MONTH_NAMES[m - 1]).join(', ')
                          : 'Todo el ano'}
                      </td>
                      <td className="py-2.5 px-4 text-slate-500 text-xs hidden sm:table-cell max-w-xs truncate">{seta.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-3 text-center">
              Precios orientativos 2025-2026. Con <Link to="/precios-mercado" className="text-amber-700 hover:underline">PLEKO</Link> puedes comparar precios de mercado en tiempo real.
            </p>
          </div>
        </section>

        {/* Section 5: Mermas de las setas */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-3 justify-center mb-4">
            <Scale className="w-7 h-7 text-amber-700" />
            <h2 className="text-3xl font-bold text-slate-900">Mermas de las setas</h2>
          </div>
          <p className="text-slate-600 text-center mb-8 max-w-2xl mx-auto">
            Las setas contienen mucha agua y tienen partes no comestibles. Conocer las mermas reales
            es fundamental para un <Link to="/escandallo" className="text-amber-700 hover:underline">escandallo preciso</Link>.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white rounded-xl border border-slate-200 overflow-hidden">
              <thead>
                <tr className="bg-amber-50">
                  <th className="text-left py-3 px-4 font-semibold text-slate-700">Variedad</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Merma limpieza</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Merma coccion</th>
                  <th className="text-center py-3 px-4 font-semibold text-slate-700">Aprovechamiento neto</th>
                </tr>
              </thead>
              <tbody>
                {SETAS.map((seta, i) => {
                  const limpMin = parseInt(seta.mermaLimpieza);
                  const cocMin = parseInt(seta.mermaCoccion);
                  const approx = 100 - limpMin - cocMin;
                  return (
                    <tr key={i} className={`border-t border-slate-100 ${i % 2 === 0 ? '' : 'bg-amber-50/30'}`}>
                      <td className="py-2.5 px-4 font-medium text-slate-800">{seta.name}</td>
                      <td className="py-2.5 px-4 text-center text-slate-600">{seta.mermaLimpieza}</td>
                      <td className="py-2.5 px-4 text-center text-slate-600">{seta.mermaCoccion}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`font-semibold ${approx >= 60 ? 'text-green-600' : approx >= 45 ? 'text-amber-600' : 'text-red-600'}`}>
                          ~{approx}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-slate-400 mt-3 text-center">
            Mermas aproximadas. Consulta la <Link to="/tabla-mermas-alimentos" className="text-amber-700 hover:underline">tabla completa de mermas</Link> para todos los alimentos.
          </p>
        </section>

        {/* Section 6: Zonas seteras */}
        <section className="bg-amber-50/50 py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 justify-center mb-4">
              <MapPin className="w-7 h-7 text-amber-700" />
              <h2 className="text-3xl font-bold text-slate-900">Las mejores zonas seteras de Espana</h2>
            </div>
            <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
              Espana es uno de los paises con mayor riqueza micologica de Europa. Estas son las zonas clave para encontrar setas de calidad profesional.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {ZONAS.map((zona, i) => (
                <div key={i} className="bg-white rounded-xl border border-amber-200 p-5 shadow-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-amber-600 flex-shrink-0" />
                    <h3 className="font-bold text-slate-900">{zona.name}</h3>
                    <span className="text-xs text-slate-400 ml-auto">{zona.region}</span>
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">{zona.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 7: Como conservar */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-center gap-3 justify-center mb-4">
            <Thermometer className="w-7 h-7 text-amber-700" />
            <h2 className="text-3xl font-bold text-slate-900">Como conservar setas frescas en tu restaurante</h2>
          </div>
          <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
            Las setas frescas son delicadas. Una buena conservacion es la diferencia entre producto premium y producto que va a la basura.
          </p>
          <div className="grid sm:grid-cols-2 gap-6">
            {CONSERVACION.map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={i} className="flex gap-4 items-start bg-white rounded-xl border border-slate-200 p-5">
                  <div className="p-2 rounded-lg bg-amber-100 flex-shrink-0">
                    <Icon className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1">{c.method}</h3>
                    <p className="text-xs text-amber-700 font-medium mb-2">Duracion: {c.duration}</p>
                    <p className="text-slate-600 text-sm leading-relaxed">{c.tips}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Section 8: Ideas para carta */}
        <section className="bg-slate-50 py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3 justify-center mb-4">
              <ChefHat className="w-7 h-7 text-amber-700" />
              <h2 className="text-3xl font-bold text-slate-900">Ideas para incluir setas de temporada en tu carta</h2>
            </div>
            <p className="text-slate-600 text-center mb-10 max-w-2xl mx-auto">
              Las setas funcionan en todas las secciones de la carta. Desde entrantes accesibles hasta platos principales premium.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {IDEAS_CARTA.map((cat, i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-5">
                  <h3 className="font-bold text-amber-700 mb-3">{cat.tipo}</h3>
                  <ul className="space-y-2">
                    {cat.ideas.map((idea, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-700">
                        <span className="text-amber-500 mt-0.5 flex-shrink-0">&#x25cf;</span>
                        {idea}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="text-sm text-slate-500 mt-6 text-center">
              Calcula el <Link to="/escandallo" className="text-amber-700 hover:underline">escandallo</Link> de cada plato con setas antes de incluirlo en tu carta. Con <Link to="/ingenieria-de-menu" className="text-amber-700 hover:underline">ingenieria de menu</Link> sabras cuales son los mas rentables.
            </p>
          </div>
        </section>

        {/* Section 9: Cultivadas vs silvestres */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Setas cultivadas vs silvestres
          </h2>
          <p className="text-slate-600 text-center mb-8 max-w-2xl mx-auto">
            Las setas cultivadas estan disponibles todo el ano y ofrecen precios estables. Son el complemento perfecto a las silvestres de temporada.
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {CULTIVADAS.map((s, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-bold text-slate-900">{s.name}</h3>
                  <span className="text-xs font-medium bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{s.price}</span>
                </div>
                <p className="text-xs text-slate-400 italic mb-2">{s.scientific} — {s.disponibilidad}</p>
                <p className="text-sm text-slate-600">{s.notes}</p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-amber-50/50 py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-10">
              Preguntas frecuentes sobre setas de temporada
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="group bg-white rounded-xl border border-amber-200 overflow-hidden">
                  <summary className="flex items-center justify-between cursor-pointer px-6 py-4 text-left font-semibold text-slate-900 hover:bg-amber-50 transition-colors">
                    {faq.q}
                    <span className="ml-4 text-slate-400 group-open:rotate-45 transition-transform text-xl">+</span>
                  </summary>
                  <div className="px-6 pb-4 text-slate-600 leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Cross-links */}
        <section className="py-12">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xl font-bold text-slate-900 mb-6 text-center">
              Guias relacionadas
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {[
                { to: '/calendario-temporada', label: 'Calendario de temporada' },
                { to: '/precios-mercado', label: 'Precios de mercado' },
                { to: '/tabla-mermas-alimentos', label: 'Tabla de mermas' },
                { to: '/escandallo', label: 'Que es un escandallo' },
                { to: '/ingenieria-de-menu', label: 'Ingenieria de menu' },
                { to: '/gestion-compras-restaurante', label: 'Gestion de compras' },
                { to: '/food-cost', label: 'Food cost' },
                { to: '/calculadora-escandallo', label: 'Calculadora escandallo' },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="flex items-center gap-2 px-4 py-3 bg-white border border-slate-200 rounded-lg hover:border-amber-300 hover:bg-amber-50 transition-colors text-sm font-medium text-slate-700"
                >
                  <Leaf className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                  <span className="truncate">{link.label}</span>
                  <ArrowRight className="w-3.5 h-3.5 ml-auto text-slate-400 flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-amber-700 to-amber-800 py-16">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Planifica tu carta estacional y controla costes con PLEKO
            </h2>
            <p className="text-amber-100 text-lg mb-8 max-w-xl mx-auto">
              Calcula el escandallo de tus platos con setas, controla mermas y maximiza el margen
              de tu carta de temporada. Todo en una sola herramienta.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-amber-800 rounded-xl font-bold text-lg hover:bg-amber-50 transition-colors shadow-lg"
            >
              Prueba gratis 14 dias <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
